import React, { useState, useEffect, useRef } from 'react';
import {
    View,
    Text,
    ScrollView,
    StyleSheet,
    TouchableOpacity,
    ActivityIndicator,
    Alert,
    TextInput,
    Platform,
    Animated,
    Dimensions,
    Modal,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import AdminLayout from '../components/layout/AdminLayout';
import {
    getFinancialOverview,
    getWallets,
    getWithdrawals,
    approveWithdrawal,
    rejectWithdrawal,
    getFinancialSettings,
    updateFinancialSetting,
    adjustEscrow,
    getAdminLoans,
    updateAdminLoanStatus,
    getAdminSavingsChallenges,
    createAdminSavingsChallenge,
    updateAdminSavingsChallenge,
    getChallengeSavingsParticipants,
    getAuditLogs,
    getFlaggedAuditLogs,
    resolveAuditFlag
} from '../api/financial';
import { T, S } from '../components/financial/FinancialStyles';


// ─────────────────────────────────────────────────────────────
// Landing-page token map (mirrors styles.css variables)
// ─────────────────────────────────────────────────────────────

const IS_WEB = Platform.OS === 'web';
const { width: SW } = Dimensions.get('window');

// ─────────────────────────────────────────────────────────────
// Mini sparkline (same visual, landing-page palette)
// ─────────────────────────────────────────────────────────────
const Sparkline = ({ data = [], color = T.blue }) => {
    const max = Math.max(...data, 1);
    return (
        <View style={{ flexDirection: 'row', alignItems: 'flex-end', gap: 2, height: 20 }}>
            {data.map((v, i) => (
                <View key={i} style={{
                    width: 3, borderRadius: 2,
                    height: Math.max(3, (v / max) * 20),
                    backgroundColor: color,
                    opacity: 0.25 + (i / data.length) * 0.75,
                }} />
            ))}
        </View>
    );
};

// ─────────────────────────────────────────────────────────────
// Animated count-up value
// ─────────────────────────────────────────────────────────────
const AnimatedValue = ({ value, style }) => {
    const anim = useRef(new Animated.Value(0)).current;
    const [disp, setDisp] = useState(0);
    useEffect(() => {
        Animated.timing(anim, { toValue: value, duration: 900, useNativeDriver: false }).start();
        anim.addListener(({ value: v }) => setDisp(Math.floor(v)));
        return () => anim.removeAllListeners();
    }, [value]);
    return <Text style={style}>KES {Number(disp).toLocaleString()}</Text>;
};

// ─────────────────────────────────────────────────────────────
// Section tag — mirrors .section-tag pill from landing page
// ─────────────────────────────────────────────────────────────
const SectionTag = ({ label }) => (
    <View style={S.sectionTag}>
        <Text style={S.sectionTagTxt}>{label.toUpperCase()}</Text>
    </View>
);

// ─────────────────────────────────────────────────────────────
// Status pill
// ─────────────────────────────────────────────────────────────
const StatusPill = ({ status }) => {
    const cfg = {
        pending_admin: { label: 'Admin Review', bg: 'rgba(245,166,35,0.12)', border: 'rgba(245,166,35,0.3)', txt: T.goldLight, dot: T.amber },
        pending: { label: 'Pending', bg: 'rgba(59,130,246,0.12)', border: 'rgba(59,130,246,0.3)', txt: T.blueLight, dot: T.blue },
        success: { label: 'Success', bg: 'rgba(16,185,129,0.12)', border: 'rgba(16,185,129,0.3)', txt: '#6ee7b7', dot: T.green },
        failed: { label: 'Failed', bg: 'rgba(239,68,68,0.12)', border: 'rgba(239,68,68,0.3)', txt: '#fca5a5', dot: T.red },
    };
    const c = cfg[status] || { label: status, bg: T.glass, border: T.glassBorder, txt: T.gray300, dot: T.gray400 };
    return (
        <View style={[S.pill, { backgroundColor: c.bg, borderColor: c.border }]}>
            <View style={[S.pillDot, { backgroundColor: c.dot }]} />
            <Text style={[S.pillTxt, { color: c.txt }]}>{c.label}</Text>
        </View>
    );
};

// ─────────────────────────────────────────────────────────────
// MAIN SCREEN
// ─────────────────────────────────────────────────────────────
const FinancialManagementScreen = () => {
    const [loading, setLoading] = useState(true);
    const [overview, setOverview] = useState(null);
    const [wallets, setWallets] = useState([]);
    const [withdrawals, setWithdrawals] = useState([]);
    const [settings, setSettings] = useState([]);
    const [challenges, setChallenges] = useState([]);
    const [loans, setLoans] = useState([]);
    const [loanFilter, setLoanFilter] = useState('all');
    const [activeTab, setActiveTab] = useState('withdrawals');
    const [newThreshold, setNewThreshold] = useState('');
    const [refreshing, setRefreshing] = useState(false);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    // Loan Settings edit state
    const [loanSettings, setLoanSettings] = useState({});
    const [savingLoanSettings, setSavingLoanSettings] = useState(false);

    // Challenge create modal
    const [createChallengeModal, setCreateChallengeModal] = useState(false);
    const [newChallenge, setNewChallenge] = useState({ title: '', description: '', cadence: 'monthly', target_amount: '', xp_reward: '50', bonus_description: '', start_date: '', end_date: '', max_participants: '', badge_icon: 'trophy-outline' });
    const [challengeLoading, setChallengeLoading] = useState(false);

    // Master-Detail selection state
    const [selectedItem, setSelectedItem] = useState(null);
    const [actionType, setActionType] = useState(null);

    // Audit & Alerts state
    const [auditLogs, setAuditLogs] = useState([]);
    const [flaggedAlerts, setFlaggedAlerts] = useState([]);
    const [auditSearch, setAuditSearch] = useState('');
    const [auditFilter, setAuditFilter] = useState('all');

    // Sliding tab indicator
    const tabs = ['withdrawals', 'wallets', 'loans', 'challenges', 'audit', 'alerts'];
    const tabW = IS_WEB ? 104 : (SW - 32) / 6;
    const tabAnim = useRef(new Animated.Value(0)).current;



    const switchTab = (idx) => {
        Animated.spring(tabAnim, { toValue: idx * tabW, useNativeDriver: true, damping: 18, stiffness: 180 }).start();
        setActiveTab(tabs[idx]);
        setSelectedItem(null); // Reset selection on tab change
        setActionType(null);
    };


    useEffect(() => { fetchData(); }, []);

    const fetchData = async (silent = false) => {
        try {
            if (!silent) setLoading(true); else setRefreshing(true);
            const [ov, wa, wi, se, ch, lo, au, fl] = await Promise.all([
                getFinancialOverview(), getWallets(), getWithdrawals(), getFinancialSettings(), getAdminSavingsChallenges(), getAdminLoans(), getAuditLogs({ limit: 100 }), getFlaggedAuditLogs()
            ]);
            if (ov.success) setOverview(ov.data);
            if (wa.success) setWallets(wa.data);
            if (wi.success) setWithdrawals(wi.data);
            if (se.success) {
                setSettings(se.data);
                const t = se.data.find(s => s.key === 'WITHDRAWAL_THRESHOLD');
                if (t) setNewThreshold(t.value);
                // Build loan settings map
                const lsMap = {};
                se.data.filter(s => s.key.startsWith('LOAN_')).forEach(s => { lsMap[s.key] = s.value; });
                setLoanSettings(lsMap);
            }
            if (ch.success) setChallenges(ch.data);
            if (lo.success) setLoans(lo.data);
            if (au.success) setAuditLogs(au.data.logs || []);
            if (fl.success) setFlaggedAlerts(fl.data.entries || []);
        } catch (e) {
            console.error(e);
            Alert.alert('Error', 'Failed to load financial data');
        } finally { setLoading(false); setRefreshing(false); }
    };


    const handleApprove = (id) =>
        Alert.alert('Approve Withdrawal', 'This will trigger an M-Pesa payment. Continue?', [
            { text: 'Cancel', style: 'cancel' },
            {
                text: 'Approve', onPress: async () => {
                    try { const r = await approveWithdrawal(id); if (r.success) { Alert.alert('Success', 'Approved'); fetchData(true); } }
                    catch (e) { Alert.alert('Error', e.response?.data?.error || 'Approval failed'); }
                }
            }
        ]);

    const handleReject = (id) => {
        if (Platform.OS === 'web') {
            const reason = window.prompt('Enter rejection reason:');
            if (reason) rejectAction(id, reason);
            return;
        }
        Alert.prompt('Reject Withdrawal', 'Enter reason:', (reason) => { if (reason) rejectAction(id, reason); });
    };

    const handleResolveFlag = async (logId, notes) => {
        try {
            const r = await resolveAuditFlag(logId, notes);
            if (r.success) { Alert.alert('Success', 'Flag resolved'); fetchData(true); }
        } catch (e) { Alert.alert('Error', 'Failed to resolve flag'); }
    };

    const rejectAction = async (id, reason) => {
        try { const r = await rejectWithdrawal(id, reason); if (r.success) { Alert.alert('Success', 'Rejected'); fetchData(true); } }
        catch { Alert.alert('Error', 'Rejection failed'); }
    };

    const handleUpdateThreshold = async () => {
        if (!newThreshold || isNaN(newThreshold)) return Alert.alert('Error', 'Enter a valid amount');
        try { const r = await updateFinancialSetting('WITHDRAWAL_THRESHOLD', newThreshold, 'Max auto-payout'); if (r.success) { Alert.alert('Updated'); fetchData(true); } }
        catch { Alert.alert('Error', 'Update failed'); }
    };

    const escrowAction = async (wId, amount, type, reason, label) => {
        try { const r = await adjustEscrow(wId, amount, type, reason); if (r.success) { Alert.alert('Success', `${label} successful`); fetchData(true); } }
        catch (e) { Alert.alert('Error', e.response?.data?.error || 'Adjustment failed'); }
    };

    const handleAdjustEscrow = (wId, curr, type) => {
        const label = type === 'escrow_topup' ? 'Top-up' : 'Withdraw';
        if (Platform.OS === 'web') {
            const amt = window.prompt(`${label} — enter amount:`);
            if (amt && !isNaN(amt)) { const rsn = window.prompt('Reason:'); if (rsn) escrowAction(wId, amt, type, rsn, label); }
            return;
        }
        Alert.prompt(`${label} Escrow`, 'Enter amount:', [
            { text: 'Cancel', style: 'cancel' },
            {
                text: label, onPress: (amt) => {
                    if (!amt || isNaN(amt)) return Alert.alert('Error', 'Invalid amount');
                    Alert.prompt('Reason', 'Provide reason:', (rsn) => { if (rsn) escrowAction(wId, amt, type, rsn, label); });
                }
            }
        ]);
    };

    const handleLoanAction = (loan, newStatus) => {
        const label = newStatus === 'active' ? 'Approve & Disburse' : 'Reject';
        const msg = newStatus === 'active'
            ? `Approve this loan? KES ${Number(loan.amount).toLocaleString()} will be disbursed to ${loan.user?.name || 'the user'}'s wallet.`
            : `Reject this loan application from ${loan.user?.name || 'the user'}?`;
        Alert.alert(label, msg, [
            { text: 'Cancel', style: 'cancel' },
            {
                text: label, style: newStatus === 'active' ? 'default' : 'destructive',
                onPress: async () => {
                    try {
                        const r = await updateAdminLoanStatus(loan.id, newStatus);
                        if (r.success) {
                            Alert.alert('Done', r.message);
                            fetchData(true);
                        }
                    } catch (e) {
                        Alert.alert('Error', e.response?.data?.error || 'Action failed');
                    }
                }
            }
        ]);
    };

    const handleSaveLoanSettings = async () => {
        setSavingLoanSettings(true);
        try {
            await Promise.all(
                Object.entries(loanSettings).map(([key, value]) =>
                    updateFinancialSetting(key, String(value), `Loan Algorithm: ${key}`)
                )
            );
            Alert.alert('✓ Saved', 'Loan scoring settings updated!');
            fetchData(true);
        } catch {
            Alert.alert('Error', 'Failed to save loan settings.');
        } finally { setSavingLoanSettings(false); }
    };

    const handleExportCSV = async () => {
        try {
            setRefreshing(true);
            const response = await api.get('/admin/finance/audit-log/export', { responseType: 'blob' });
            if (IS_WEB) {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `audit-log-${new Date().toISOString().slice(0, 10)}.csv`);
                document.body.appendChild(link);
                link.click();
                link.remove();
            } else {
                 Alert.alert('Info', 'CSV Export is available on the Web Admin Dashboard.');
            }
        } catch (e) {
            Alert.alert('Error', 'Failed to export CSV');
        } finally {
            setRefreshing(false);
        }
    };


    // Filtered data
    const filtered = withdrawals.filter(w => {
        const q = search.toLowerCase();
        const matchQ = !q || (w.wallet?.user?.name || '').toLowerCase().includes(q) || (w.wallet?.user?.phone || '').includes(q);
        const matchS = statusFilter === 'all' || w.status === statusFilter;
        return matchQ && matchS;
    });
    const pendingCount = withdrawals.filter(w => w.status === 'pending_admin').length;
    const pendingLoansCount = loans.filter(l => l.status === 'pending').length;
    const filteredLoans = loanFilter === 'all' ? loans : loans.filter(l => l.status === loanFilter);

    // Filter audit logs
    const filteredAudit = auditLogs.filter(a => {
        const q = auditSearch.toLowerCase();
        const matchQ = !q || a.event_type.toLowerCase().includes(q) || (a.affectedUser?.name || '').toLowerCase().includes(q);
        let matchF = true;
        if (auditFilter === 'flagged') matchF = a.flagged;
        if (auditFilter === 'success') matchF = a.status === 'success';
        if (auditFilter === 'failed') matchF = a.status === 'failed';
        return matchQ && matchF;
    });

    const pendingAlertsCount = flaggedAlerts.length;

    // Auto-select first item in list on tab change
    const lastActiveTab = useRef(activeTab);
    useEffect(() => {
        if (!selectedItem || lastActiveTab.current !== activeTab) {
            lastActiveTab.current = activeTab;
            if (activeTab === 'withdrawals' && filtered.length > 0) { setSelectedItem(filtered[0]); setActionType('withdrawal'); }
            else if (activeTab === 'wallets' && wallets.length > 0) { setSelectedItem(wallets[0]); setActionType('wallet'); }
            else if (activeTab === 'loans' && filteredLoans.length > 0) { setSelectedItem(filteredLoans[0]); setActionType('loan'); }
            else if (activeTab === 'challenges' && challenges.length > 0) { setSelectedItem(challenges[0]); setActionType('challenge'); }
            else if (activeTab === 'audit' && filteredAudit.length > 0) { setSelectedItem(filteredAudit[0]); setActionType('audit'); }
            else if (activeTab === 'alerts' && flaggedAlerts.length > 0) { setSelectedItem(flaggedAlerts[0]); setActionType('alert'); }
            else { setSelectedItem(null); setActionType(null); }
        }
    }, [activeTab, filtered, wallets, filteredLoans, challenges, filteredAudit, flaggedAlerts, selectedItem]);

    // ─── Metric card config ──────────────────────────────────────
    const metrics = [
        {
            icon: 'business-outline',
            label: 'System Treasury',
            sub: 'Global reserves & fees',
            value: Number(overview?.system_wallet?.escrow_balance || 0),
            accent: T.blue,
            accentBg: 'rgba(45,140,240,0.12)',
            accentBorder: 'rgba(45,140,240,0.25)',
            sparkline: [4, 7, 5, 9, 6, 12, 8, 14, 10, 16],
            actionType: 'escrow_topup', // Replaces inline onTopup handler for master-detail
        },
        {
            icon: 'shield-checkmark-outline',
            label: 'User Escrow Funds',
            sub: 'Locked funds in orders',
            value: Number(overview?.total_escrow || 0),
            accent: T.green,
            accentBg: 'rgba(16,185,129,0.12)',
            accentBorder: 'rgba(16,185,129,0.25)',
            sparkline: [6, 9, 5, 13, 8, 7, 15, 11, 10, 17],
        },
        {
            icon: 'wallet-outline',
            label: 'Total User Balances',
            sub: 'Total withdrawable funds',
            value: Number(overview?.total_balance || 0),
            accent: T.gold,
            accentBg: 'rgba(245,166,35,0.12)',
            accentBorder: 'rgba(245,166,35,0.25)',
            sparkline: [3, 6, 4, 9, 12, 7, 10, 14, 13, 19],
        },
    ];

    // ─── Loading ─────────────────────────────────────────────────
    if (loading) {
        return (
            <AdminLayout>
                <View style={S.loadWrap}>
                    <ActivityIndicator size="large" color={T.blue} />
                    <Text style={S.loadTxt}>Loading financial data…</Text>
                </View>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <ScrollView style={S.root} contentContainerStyle={S.content} showsVerticalScrollIndicator={false}>

                {/* ── Decorative background orbs (web only) ─────── */}
                {IS_WEB && (
                    <View style={S.bgOrbs} pointerEvents="none">
                        <View style={[S.orb, S.orb1]} />
                        <View style={[S.orb, S.orb2]} />
                    </View>
                )}

                {/* ── Page header ───────────────────────────────── */}
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 12, paddingHorizontal: 2 }}>
                    <View>
                        <Text style={S.pageTitle}>Financial Hub</Text>
                        <Text style={{ fontSize: 11, color: T.gray400, marginTop: 4 }}>Real-time treasury & payout management</Text>
                    </View>
                    <TouchableOpacity style={{ flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: 8, paddingHorizontal: 14, backgroundColor: 'rgba(45,140,240,0.1)', borderRadius: T.rMd, borderWidth: 1, borderColor: 'rgba(45,140,240,0.2)' }} onPress={() => fetchData(true)} activeOpacity={0.8}>
                        {refreshing ? <ActivityIndicator size="small" color={T.blueLight} /> : <Ionicons name="refresh-outline" size={14} color={T.blueLight} />}
                        <Text style={{ color: T.blueLight, fontSize: 11, fontWeight: '600' }}>Refresh Data</Text>
                    </TouchableOpacity>
                </View>

                {/* ── Metrics Row ───────────────────────────────── */}
                <View style={{ width: '100%', marginBottom: 16 }}>
                    <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 8, paddingVertical: 2, flexGrow: 1 }}>
                        {metrics.map((m, i) => (
                            <View key={i} style={[S.metricCard, { borderColor: m.accentBorder, minWidth: IS_WEB ? '32%' : 160, flex: 1 }]}>
                                <View style={[S.metricAccentTop, { backgroundColor: m.accent }]} />
                                <View style={S.metricHead}>
                                    <View style={[S.metricIconBox, { backgroundColor: m.accentBg }]}>
                                        <Ionicons name={m.icon} size={14} color={m.accent} />
                                    </View>
                                    {m.actionType && overview?.system_wallet && (
                                        <TouchableOpacity
                                            style={[S.metricActionChip, { borderColor: m.accentBorder }]}
                                            onPress={() => {
                                                setSelectedItem(overview.system_wallet);
                                                setActionType(m.actionType);
                                            }}
                                        >
                                            <Ionicons name="add" size={10} color={m.accent} />
                                            <Text style={[S.metricActionTxt, { color: m.accent }]}>Manage</Text>
                                        </TouchableOpacity>
                                    )}
                                </View>
                                <Text style={S.metricLabel}>{m.label}</Text>
                                <AnimatedValue value={m.value} style={[S.metricValue, { color: m.accent, fontSize: 16 }]} />
                                <View style={S.metricFoot}>
                                    <Text style={S.metricSub}>{m.sub}</Text>
                                    <Sparkline data={m.sparkline} color={m.accent} />
                                </View>
                            </View>
                        ))}
                    </ScrollView>
                </View>

                {/* ── Stats strip (mirrors .stats section) ─────── */}
                <View style={S.statsStrip}>
                    {[
                        { val: withdrawals.length, lbl: 'Total Requests', clr: T.white },
                        { val: pendingCount, lbl: 'Pending Review', clr: T.goldLight },
                        { val: wallets.length, lbl: 'Active Wallets', clr: '#6ee7b7' },
                    ].map((s, i) => (
                        <React.Fragment key={i}>
                            {i > 0 && <View style={S.statsDivider} />}
                            <View style={S.statItem}>
                                <Text style={[S.statVal, { color: s.clr }]}>{s.val}</Text>
                                <Text style={S.statLbl}>{s.lbl}</Text>
                            </View>
                        </React.Fragment>
                    ))}
                </View>

                <View style={S.tabBar}>
                    {/* Sliding pill indicator */}
                    <Animated.View style={[S.tabIndicator, { width: tabW, transform: [{ translateX: tabAnim }] }]} />
                    {tabs.map((tab, i) => {
                        const active = activeTab === tab;
                        const icons = { withdrawals: 'swap-vertical-outline', wallets: 'wallet-outline', loans: 'cash-outline', challenges: 'trophy-outline', audit: 'list-outline', alerts: 'alert-circle-outline', settings: 'settings-outline' };
                        const labels = { withdrawals: 'Payouts', wallets: 'Wallets', loans: 'Loans', challenges: 'Challenges', audit: 'Audit Trail', alerts: 'Alerts', settings: 'Settings' };
                        return (
                            <TouchableOpacity key={tab} style={[S.tabBtn, { width: tabW }]} onPress={() => switchTab(i)} activeOpacity={0.75}>
                                <Ionicons name={icons[tab]} size={12} color={active ? T.blueLight : T.gray500} />
                                <Text style={[S.tabLbl, active && S.tabLblActive]}>
                                    {labels[tab]}
                                    {tab === 'withdrawals' && pendingCount > 0
                                        ? <Text style={{ color: T.goldLight }}> {pendingCount}</Text>
                                        : null}
                                    {tab === 'loans' && pendingLoansCount > 0
                                        ? <Text style={{ color: T.amber }}> {pendingLoansCount}</Text>
                                        : null}
                                    {tab === 'challenges' && challenges.length > 0
                                        ? <Text style={{ color: T.cyan }}> {challenges.length}</Text>
                                        : null}
                                    {tab === 'alerts' && pendingAlertsCount > 0
                                        ? <Text style={{ color: T.red }}> {pendingAlertsCount}</Text>
                                        : null}
                                </Text>
                            </TouchableOpacity>
                        );
                    })}
                    <TouchableOpacity
                        style={[S.tabBtn, { marginLeft: 'auto', paddingHorizontal: 20 }]}
                        onPress={() => { setSelectedItem({ id: 'settings' }); setActionType('settings'); }}
                        activeOpacity={0.75}
                    >
                        <Ionicons name="settings-outline" size={12} color={actionType === 'settings' ? T.blueLight : T.gray500} />
                        <Text style={[S.tabLbl, actionType === 'settings' && S.tabLblActive]}>Settings</Text>
                    </TouchableOpacity>
                </View>

                {/* ── Dashboard Split ────────────────────────────── */}
                <View style={S.splitGrid}>

                    {/* LEFT COLUMN: Management Tabs */}
                    <View style={S.splitLeft}>
                            {/* ══════════ WITHDRAWALS ════════════════════════ */}
                            {activeTab === 'withdrawals' && (
                                <View>
                                    <View style={S.toolbar}>
                                        <View style={S.searchWrap}>
                                            <Ionicons name="search-outline" size={13} color={T.gray500} />
                                            <TextInput style={S.searchInput} placeholder="Search payouts…" placeholderTextColor={T.gray600} value={search} onChangeText={setSearch} />
                                        </View>
                                        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 6 }}>
                                            {[{ key: 'all', lbl: 'All' }, { key: 'pending_admin', lbl: 'Review' }, { key: 'pending', lbl: 'Pending' }, { key: 'success', lbl: 'Success' }, { key: 'failed', lbl: 'Failed' }].map(f => (
                                                <TouchableOpacity key={f.key} style={[S.filterChip, statusFilter === f.key && S.filterChipActive]} onPress={() => setStatusFilter(f.key)}>
                                                    <Text style={[S.filterChipTxt, statusFilter === f.key && S.filterChipTxtActive]}>{f.lbl}</Text>
                                                </TouchableOpacity>
                                            ))}
                                        </ScrollView>
                                    </View>

                                    <View style={S.listHeader}>
                                        <SectionTag label="Payout Requests" />
                                        <Text style={S.listCount}>{filtered.length} records</Text>
                                    </View>

                                    {filtered.length === 0 ? (
                                        <View style={S.emptyState}><Text style={S.emptyTitle}>No Payouts</Text></View>
                                    ) : (
                                        filtered.map(item => <WithdrawalCard key={item.id} item={item} isSelected={selectedItem?.id === item.id} onSelect={() => { setSelectedItem(item); setActionType('withdrawal'); }} />)
                                    )}
                                </View>
                            )}

                            {/* ══════════ WALLETS ════════════════════════════ */}
                            {activeTab === 'wallets' && (
                                <View>
                                    <View style={S.listHeader}>
                                        <SectionTag label="User Wallets" />
                                        <Text style={S.listCount}>{wallets.length} wallets</Text>
                                    </View>
                                    {wallets.map(w => <WalletCard key={w.id} wallet={w} isSelected={selectedItem?.id === w.id} onSelect={() => { setSelectedItem(w); setActionType('wallet'); }} />)}
                                </View>
                            )}

                            {/* ══════════ LOANS ══════════════════════════════ */}
                            {activeTab === 'loans' && (
                                <View>
                                    {/* Loan Stats Strip */}
                                    <View style={[S.statsStrip, { marginBottom: 12 }]}>
                                        {[
                                            { val: loans.filter(l => l.status === 'pending').length, lbl: 'Pending', clr: T.amber },
                                            { val: loans.filter(l => l.status === 'active').length, lbl: 'Active', clr: T.green },
                                            { val: loans.filter(l => l.status === 'paid').length, lbl: 'Paid', clr: T.blueLight },
                                            { val: `KES ${Number(loans.filter(l=>['active','approved'].includes(l.status)).reduce((s,l)=>s+parseFloat(l.balance||0),0)).toLocaleString()}`, lbl: 'Outstanding', clr: T.red },
                                        ].map((s, i) => (
                                            <React.Fragment key={i}>
                                                {i > 0 && <View style={S.statsDivider} />}
                                                <View style={S.statItem}>
                                                    <Text style={[S.statVal, { color: s.clr, fontSize: 13 }]}>{s.val}</Text>
                                                    <Text style={S.statLbl}>{s.lbl}</Text>
                                                </View>
                                            </React.Fragment>
                                        ))}
                                    </View>

                                    {/* Filter chips */}
                                    <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 6, marginBottom: 10 }}>
                                        {[{ key: 'all', lbl: 'All' }, { key: 'pending', lbl: 'Pending' }, { key: 'active', lbl: 'Active' }, { key: 'paid', lbl: 'Paid' }, { key: 'rejected', lbl: 'Rejected' }, { key: 'defaulted', lbl: 'Defaulted' }].map(f => (
                                            <TouchableOpacity key={f.key} style={[S.filterChip, loanFilter === f.key && S.filterChipActive]} onPress={() => setLoanFilter(f.key)}>
                                                <Text style={[S.filterChipTxt, loanFilter === f.key && S.filterChipTxtActive]}>{f.lbl}</Text>
                                            </TouchableOpacity>
                                        ))}
                                    </ScrollView>

                                    <View style={S.listHeader}>
                                        <SectionTag label="Loan Applications" />
                                        <Text style={S.listCount}>{filteredLoans.length} records</Text>
                                    </View>

                                    {filteredLoans.length === 0 ? (
                                        <View style={S.emptyState}>
                                            <Ionicons name="cash-outline" size={22} color={T.green} />
                                            <Text style={S.emptyTitle}>No Loans Found</Text>
                                        </View>
                                    ) : (
                                        filteredLoans.map(loan => <LoanCard key={loan.id} loan={loan} isSelected={selectedItem?.id === loan.id} onSelect={() => { setSelectedItem(loan); setActionType('loan'); }} />)
                                    )}
                                </View>
                            )}

                            {/* ══════════ CHALLENGES ═════════════════════════ */}
                            {activeTab === 'challenges' && (
                                <View>
                                    <View style={S.listHeader}>
                                        <SectionTag label="Savings Challenges" />
                                        <TouchableOpacity style={S.createChallengeBtn} onPress={() => { setSelectedItem({ id: 'new_challenge'}); setActionType('create_challenge'); }}>
                                            <Ionicons name="add" size={12} color={T.navy} />
                                            <Text style={S.createChallengeBtnTxt}>New</Text>
                                        </TouchableOpacity>
                                    </View>

                                    {challenges.length === 0 ? (
                                        <View style={S.emptyState}>
                                            <Ionicons name="trophy-outline" size={22} color={T.goldLight} />
                                            <Text style={S.emptyTitle}>No active challenges</Text>
                                        </View>
                                    ) : (
                                        challenges.map(c => <AdminChallengeCard key={c.id} challenge={c} isSelected={selectedItem?.id === c.id} onSelect={() => { setSelectedItem(c); setActionType('challenge'); }} />)
                                    )}
                                </View>
                            )}

                            {/* ══════════ AUDIT TRAIL ════════════════════════ */}
                            {activeTab === 'audit' && (
                                <View>
                                    <View style={S.toolbar}>
                                        <View style={S.searchWrap}>
                                            <Ionicons name="search-outline" size={13} color={T.gray500} />
                                            <TextInput style={S.searchInput} placeholder="Search events…" placeholderTextColor={T.gray600} value={auditSearch} onChangeText={setAuditSearch} />
                                        </View>
                                        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 6 }}>
                                            {[{ key: 'all', lbl: 'All' }, { key: 'flagged', lbl: 'Flagged' }, { key: 'success', lbl: 'Success' }, { key: 'failed', lbl: 'Failed' }].map(f => (
                                                <TouchableOpacity key={f.key} style={[S.filterChip, auditFilter === f.key && S.filterChipActive]} onPress={() => setAuditFilter(f.key)}>
                                                    <Text style={[S.filterChipTxt, auditFilter === f.key && S.filterChipTxtActive]}>{f.lbl}</Text>
                                                </TouchableOpacity>
                                            ))}
                                        </ScrollView>
                                    </View>

                                    <View style={S.listHeader}>
                                        <SectionTag label="Transactions Log" />
                                        <TouchableOpacity style={[S.createChallengeBtn, { backgroundColor: 'rgba(59,130,246,0.1)' }]} onPress={handleExportCSV}>
                                            <Ionicons name="download-outline" size={12} color={T.blueLight} />
                                            <Text style={[S.createChallengeBtnTxt, { color: T.blueLight }]}>Export CSV</Text>
                                        </TouchableOpacity>
                                    </View>

                                    {filteredAudit.length === 0 ? (
                                        <View style={S.emptyState}>
                                            <Ionicons name="list-outline" size={22} color={T.gray500} />
                                            <Text style={S.emptyTitle}>No audit logs found</Text>
                                        </View>
                                    ) : (
                                        filteredAudit.map(log => <AuditCard key={log.id} log={log} isSelected={selectedItem?.id === log.id} onSelect={() => { setSelectedItem(log); setActionType('audit'); }} />)
                                    )}
                                </View>
                            )}

                            {/* ══════════ ALERTS (FLAGGED) ════════════════════════ */}
                            {activeTab === 'alerts' && (
                                <View>
                                    <View style={S.listHeader}>
                                        <SectionTag label="Security & Anomalies" />
                                        <Text style={S.listCount}>{flaggedAlerts.length} unresolved</Text>
                                    </View>

                                    {flaggedAlerts.length === 0 ? (
                                        <View style={S.emptyState}>
                                            <Ionicons name="shield-checkmark-outline" size={32} color={T.green} />
                                            <Text style={S.emptyTitle}>All Clear</Text>
                                            <Text style={S.emptySub}>No flagged activities or anomalies detected.</Text>
                                        </View>
                                    ) : (
                                        flaggedAlerts.map(alert => <AlertCard key={alert.id} alert={alert} isSelected={selectedItem?.id === alert.id} onSelect={() => { setSelectedItem(alert); setActionType('alert'); }} />)
                                    )}
                                </View>
                            )}

                    </View>

                    {/* ─── Hairline column separator ─────────────── */}
                    <View style={S.columnDivider} />

                    {/* RIGHT COLUMN: DETAIL VIEW (Master-Detail panel) */}
                    <View style={S.splitRight}>
                        {!selectedItem ? (
                            <View style={S.emptyDetailPane}>
                                <Ionicons name="layers-outline" size={32} color={T.gray500} />
                                <Text style={S.emptyDetailTitle}>No Item Selected</Text>
                                <Text style={S.emptyDetailSub}>Select an item from the list to view its details and management actions.</Text>
                            </View>
                        ) : (
                            <DetailPanel 
                                item={selectedItem} 
                                type={actionType} 
                                onClose={() => { setSelectedItem(null); setActionType(null); }}
                                escrowAction={escrowAction}
                                handleApprove={handleApprove}
                                rejectAction={rejectAction}
                                handleLoanAction={handleLoanAction}
                                handleResolveFlag={handleResolveFlag}
                                fetchData={fetchData}
                                loanSettings={loanSettings}
                                setLoanSettings={setLoanSettings}
                                savingLoanSettings={savingLoanSettings}
                                handleSaveLoanSettings={handleSaveLoanSettings}
                                newThreshold={newThreshold}
                                setNewThreshold={setNewThreshold}
                                handleUpdateThreshold={handleUpdateThreshold}
                                settings={settings}
                                newChallenge={newChallenge}
                                setNewChallenge={setNewChallenge}
                                challengeLoading={challengeLoading}
                                setChallengeLoading={setChallengeLoading}
                                onEditChallenge={(challenge) => { setSelectedItem(challenge); setActionType('edit_challenge'); }}
                            />
                        )}
                    </View>
                </View>

                <View style={{ height: 28 }} />
            </ScrollView>
        </AdminLayout>
    );
};

// ─────────────────────────────────────────────────────────────
// Admin Challenge Card component (Master List Item)
// ─────────────────────────────────────────────────────────────
const AdminChallengeCard = ({ challenge, onSelect, isSelected }) => {
    const cadenceColor = { daily: T.cyan, weekly: T.violet, monthly: T.goldLight };
    const statusColor = { upcoming: T.blueLight, active: T.green, completed: T.goldLight, cancelled: T.red };
    const isExpired = new Date(challenge.end_date) < new Date();
    const cc = cadenceColor[challenge.cadence] || T.gray400;
    const sc = isExpired ? T.red : (statusColor[challenge.status] || T.gray400);
    const statusLabel = isExpired ? 'ENDED' : challenge.status?.toUpperCase();

    return (
        <TouchableOpacity 
            style={[S.challengeCard, isSelected && { borderColor: T.blueLight, backgroundColor: 'rgba(59,130,246,0.05)' }]} 
            onPress={onSelect}
            activeOpacity={0.8}
        >
            <View style={[S.challengeAccentLine, { backgroundColor: cc }]} />
            <View style={S.challengeCardHead}>
                <View style={[S.challengeIconBox, { backgroundColor: cc + '20' }]}>
                    <Ionicons name={challenge.badge_icon || 'trophy-outline'} size={16} color={cc} />
                </View>
                <View style={{ flex: 1 }}>
                    <Text style={S.challengeTitle}>{challenge.title}</Text>
                    <View style={{ flexDirection: 'row', gap: 6, marginTop: 3, flexWrap: 'wrap' }}>
                        <View style={[S.microPill, { backgroundColor: cc + '18', borderColor: cc + '40' }]}>
                            <Text style={[S.microPillTxt, { color: cc }]}>{challenge.cadence?.toUpperCase()}</Text>
                        </View>
                        <View style={[S.microPill, { backgroundColor: sc + '18', borderColor: sc + '40' }]}>
                            <Text style={[S.microPillTxt, { color: sc }]}>{statusLabel}</Text>
                        </View>
                    </View>
                </View>
            </View>

            <View style={S.challengeStatsRow}>
                {[
                    { label: 'Target', val: `KES ${Number(challenge.target_amount).toLocaleString()}` },
                    { label: 'XP', val: `+${challenge.xp_reward}`, color: T.goldLight },
                    { label: 'Joined', val: challenge.participant_count || 0 },
                ].map((s, i) => (
                    <View key={i} style={S.challengeStat}>
                        <Text style={[S.challengeStatVal, s.color && { color: s.color }]}>{s.val}</Text>
                        <Text style={S.challengeStatLbl}>{s.label}</Text>
                    </View>
                ))}
            </View>
        </TouchableOpacity>
    );
};



// ─────────────────────────────────────────────────────────────
// Loan Card — for admin loan management (Master List Item)
// ─────────────────────────────────────────────────────────────
const LoanCard = ({ loan, onSelect, isSelected }) => {
    const isPending = loan.status === 'pending';
    const amount = Number(loan.amount);
    const initials = (loan.user?.name || '?')[0].toUpperCase();

    const statusColors = { pending: T.amber, active: T.green, paid: T.blueLight, rejected: T.red, defaulted: '#ff6b6b', approved: T.violet };
    const statusColor = statusColors[loan.status] || T.gray400;

    return (
        <TouchableOpacity 
            style={[
                S.txCard, 
                isPending && { borderColor: 'rgba(245,166,35,0.3)' },
                isSelected && { borderColor: T.blueLight, backgroundColor: 'rgba(59,130,246,0.05)' }
            ]} 
            onPress={onSelect}
            activeOpacity={0.8}
        >
            {isPending && <View style={[S.txAccentTop, { backgroundColor: T.amber }]} />}

            <View style={S.txRow}>
                {/* Avatar */}
                <View style={[S.txAvatar, { backgroundColor: isPending ? 'rgba(245,166,35,0.12)' : 'rgba(16,185,129,0.12)' }]}>
                    <Text style={[S.txAvatarTxt, { color: isPending ? T.amber : T.green }]}>{initials}</Text>
                </View>

                {/* Info */}
                <View style={{ flex: 1 }}>
                    <Text style={S.txName}>{loan.user?.name || 'Unknown'}</Text>
                    <Text style={S.txPhone}>{loan.user?.phone || loan.user?.email || '—'}</Text>
                    <Text style={S.txDate}>{new Date(loan.createdAt).toLocaleDateString('en-KE', { day: '2-digit', month: 'short', year: 'numeric' })}</Text>
                </View>

                {/* Amount + status */}
                <View style={{ alignItems: 'flex-end', gap: 4 }}>
                    <Text style={[S.txAmount, { color: isPending ? T.goldLight : T.white, fontSize: 14 }]}>
                        KES {amount.toLocaleString()}
                    </Text>
                    <View style={[S.pill, { backgroundColor: statusColor + '20', borderColor: statusColor + '50' }]}>
                        <View style={[S.pillDot, { backgroundColor: statusColor }]} />
                        <Text style={[S.pillTxt, { color: statusColor }]}>{loan.status?.toUpperCase()}</Text>
                    </View>
                </View>
            </View>
        </TouchableOpacity>
    );
};

// ─────────────────────────────────────────────────────────────
// Withdrawal card (Master List Item)
// ─────────────────────────────────────────────────────────────
const WithdrawalCard = ({ item, onSelect, isSelected }) => {
    const isPending = item.status === 'pending_admin';
    const amount = Number(item.amount);
    const isLarge = amount >= 5000;
    const initials = (item.wallet?.user?.name || '?')[0].toUpperCase();
    const avatarBg = isPending ? 'rgba(245,166,35,0.15)' : 'rgba(45,140,240,0.12)';
    const avatarClr = isPending ? T.goldLight : T.blueLight;

    return (
        <TouchableOpacity 
            style={[
                S.txCard, 
                isPending && S.txCardPending,
                isSelected && { borderColor: T.blueLight, backgroundColor: 'rgba(59,130,246,0.05)' }
            ]} 
            onPress={onSelect}
            activeOpacity={0.8}
        >
            {/* Hover accent line top */}
            {isPending && <View style={[S.txAccentTop, { backgroundColor: T.amber }]} />}

            <View style={S.txRow}>
                {/* Avatar */}
                <View style={[S.txAvatar, { backgroundColor: avatarBg }]}>
                    <Text style={[S.txAvatarTxt, { color: avatarClr }]}>{initials}</Text>
                </View>

                {/* Info */}
                <View style={{ flex: 1 }}>
                    <Text style={S.txName}>{item.wallet?.user?.name || 'Unknown'}</Text>
                    <Text style={S.txPhone}>{item.wallet?.user?.phone || '—'}</Text>
                    <Text style={S.txDate}>
                        {new Date(item.createdAt).toLocaleDateString('en-KE', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </Text>
                </View>

                {/* Amount + status */}
                <View style={{ alignItems: 'flex-end', gap: 6 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                        {isLarge && <Ionicons name="alert-circle" size={10} color={T.amber} />}
                        <Text style={[S.txAmount, { color: isLarge ? T.goldLight : T.white }]}>
                            KES {amount.toLocaleString()}
                        </Text>
                    </View>
                    <StatusPill status={item.status} />
                </View>
            </View>
        </TouchableOpacity>
    );
};

// ─────────────────────────────────────────────────────────────
// Wallet card (Master List Item)
// ─────────────────────────────────────────────────────────────
const WalletCard = ({ wallet, onSelect, isSelected }) => {
    const initials = (wallet.user?.name || '?')[0].toUpperCase();
    return (
        <TouchableOpacity 
            style={[
                S.walletCard,
                isSelected && { borderColor: T.blueLight, backgroundColor: 'rgba(59,130,246,0.05)' }
            ]} 
            onPress={onSelect}
            activeOpacity={0.8}
        >
            <View style={[S.walletAvatar, { backgroundColor: 'rgba(45,140,240,0.12)' }]}>
                <Text style={[S.walletAvatarTxt, { color: T.blueLight }]}>{initials}</Text>
            </View>
            <View style={{ flex: 1 }}>
                <Text style={S.walletName}>{wallet.user?.name}</Text>
                <Text style={S.walletEmail}>{wallet.user?.email}</Text>
            </View>
            {/* Balances */}
            <View style={S.walletBals}>
                <View>
                    <Text style={S.walletBalLbl}>Balance</Text>
                    <Text style={[S.walletBalVal, { color: '#6ee7b7' }]}>
                        KES {Number(wallet.balance).toLocaleString()}
                    </Text>
                </View>
                <View style={{ alignItems: 'flex-end' }}>
                    <Text style={S.walletBalLbl}>Escrow</Text>
                    <Text style={[S.walletBalVal, { color: T.goldLight }]}>
                        KES {Number(wallet.escrow_balance).toLocaleString()}
                    </Text>
                </View>
            </View>
        </TouchableOpacity>
    );
};

// ─────────────────────────────────────────────────────────────
// DETAIL VIEWS (Right Panel Components)
// ─────────────────────────────────────────────────────────────

const DetailPanel = ({ 
    item, type, onClose, escrowAction, handleApprove, rejectAction, handleLoanAction, handleResolveFlag, fetchData,
    loanSettings, setLoanSettings, savingLoanSettings, handleSaveLoanSettings, newThreshold, setNewThreshold, handleUpdateThreshold, settings,
    newChallenge, setNewChallenge, challengeLoading, setChallengeLoading,
    onEditChallenge
}) => {
    switch (type) {
        case 'wallet': return <WalletDetail wallet={item} onClose={onClose} escrowAction={escrowAction} />;
        case 'escrow_topup': 
        case 'escrow_withdrawal': 
            return <WalletDetail wallet={item} onClose={onClose} escrowAction={escrowAction} isTreasury initialType={type} />;
        case 'withdrawal': return <WithdrawalDetail item={item} onClose={onClose} onApprove={handleApprove} rejectAction={rejectAction} />;
        case 'loan': return <LoanDetail loan={item} onClose={onClose} onAction={handleLoanAction} />;
        case 'challenge': return <ChallengeDetail challenge={item} onClose={onClose} onUpdate={fetchData} onEdit={onEditChallenge} />;
        case 'audit': return <AuditDetail log={item} onClose={onClose} />;
        case 'alert': return <AlertDetail alert={item} onClose={onClose} onResolve={handleResolveFlag} />;
        case 'settings': return <SettingsDetail onClose={onClose} loanSettings={loanSettings} setLoanSettings={setLoanSettings} savingLoanSettings={savingLoanSettings} handleSaveLoanSettings={handleSaveLoanSettings} newThreshold={newThreshold} setNewThreshold={setNewThreshold} handleUpdateThreshold={handleUpdateThreshold} settings={settings} />;
        case 'create_challenge': return <CreateChallengeDetail onClose={onClose} onUpdate={fetchData} newChallenge={newChallenge} setNewChallenge={setNewChallenge} challengeLoading={challengeLoading} setChallengeLoading={setChallengeLoading} />;
        case 'edit_challenge': return <EditChallengeDetail challenge={item} onClose={onClose} onUpdate={fetchData} />;
        default: return null;
    }
};

const WalletDetail = ({ wallet, onClose, escrowAction, isTreasury, initialType = 'escrow_topup' }) => {
    const [amt, setAmt] = useState('');
    const [reason, setReason] = useState('');
    const [txType, setTxType] = useState(initialType);
    const [loading, setLoading] = useState(false);
    
    const initials = isTreasury ? 'TR' : (wallet?.user?.name || '?')[0].toUpperCase();
    const title = isTreasury ? 'System Treasury' : wallet?.user?.name;
    const balance = isTreasury ? wallet.escrow_balance : wallet.escrow_balance;

    const handleSubmit = async () => {
        if (!amt || isNaN(amt)) return Alert.alert('Error', 'Please enter a valid amount.');
        if (!reason.trim()) return Alert.alert('Error', 'Please provide a reason for the audit log.');
        
        const label = txType === 'escrow_topup' ? 'Top-up' : 'Withdraw';
        Alert.alert('Confirm Transaction', `Execute ${label} of KES ${Number(amt).toLocaleString()} for ${title}?`, [
            { text: 'Cancel', style: 'cancel' },
            { text: 'Confirm', style: txType === 'escrow_topup' ? 'default' : 'destructive', onPress: async () => {
                setLoading(true);
                await escrowAction(wallet.id, amt, txType, reason, label);
                setLoading(false);
                setAmt('');
                setReason('');
            }}
        ]);
    };

    return (
        <ScrollView style={S.detailPane} showsVerticalScrollIndicator={false}>
            <View style={S.detailHeader}>
                <TouchableOpacity onPress={onClose}><Ionicons name="close" size={20} color={T.gray400} /></TouchableOpacity>
                <Text style={S.detailHeaderTitle}>{isTreasury ? 'Treasury Management' : 'Wallet Escrow'}</Text>
            </View>

            <View style={S.detailProfile}>
                <View style={[S.detailAvatar, { backgroundColor: isTreasury ? 'rgba(245,166,35,0.1)' : 'rgba(45,140,240,0.1)' }]}>
                    <Text style={[S.detailAvatarTxt, { color: isTreasury ? T.goldLight : T.blueLight }]}>{initials}</Text>
                </View>
                <Text style={S.detailName}>{title}</Text>
                <Text style={S.detailSub}>{isTreasury ? 'Global Platform Reserves' : wallet?.user?.phone}</Text>
                <View style={S.detailBalBox}>
                    <Text style={S.detailBalLbl}>Available Escrow</Text>
                    <Text style={S.detailBalVal}>KES {Number(balance || 0).toLocaleString()}</Text>
                </View>
            </View>

            {/* Finacle-style Action Form */}
            <View style={S.detailSection}>
                <Text style={S.detailSectionTitle}>EXECUTE TRANSACTION</Text>
                
                <View style={S.txTypeToggle}>
                    <TouchableOpacity style={[S.txTypeBtn, txType === 'escrow_topup' && S.txTypeBtnActiveT]} onPress={() => setTxType('escrow_topup')}>
                        <Text style={[S.txTypeTxt, txType === 'escrow_topup' && { color: T.green }]}>Credit (Top-up)</Text>
                    </TouchableOpacity>
                    <TouchableOpacity style={[S.txTypeBtn, txType === 'escrow_withdrawal' && S.txTypeBtnActiveW]} onPress={() => setTxType('escrow_withdrawal')}>
                        <Text style={[S.txTypeTxt, txType === 'escrow_withdrawal' && { color: T.red }]}>Debit (Withdraw)</Text>
                    </TouchableOpacity>
                </View>

                <Text style={S.detailFieldLbl}>Transaction Amount (KES)</Text>
                <TextInput style={S.detailInput} placeholder="e.g. 1000" placeholderTextColor={T.gray600} keyboardType="numeric" value={amt} onChangeText={setAmt} />

                <Text style={S.detailFieldLbl}>Audit Reason / Remarks</Text>
                <TextInput style={[S.detailInput, { height: 60, textAlignVertical: 'top' }]} placeholder="Required for compliance logging" placeholderTextColor={T.gray600} multiline value={reason} onChangeText={setReason} />

                <TouchableOpacity 
                    style={[S.detailSubmitBtn, { backgroundColor: txType === 'escrow_topup' ? T.green : T.red }]} 
                    onPress={handleSubmit} disabled={loading}
                >
                    {loading ? <ActivityIndicator size="small" color={T.white} /> : 
                        <Text style={S.detailSubmitTxt}>Authorise {txType === 'escrow_topup' ? 'Credit' : 'Debit'}</Text>
                    }
                </TouchableOpacity>
            </View>
        </ScrollView>
    );
};

const WithdrawalDetail = ({ item, onClose, onApprove, rejectAction }) => {
    const [reason, setReason] = useState('');
    const [loading, setLoading] = useState(false);
    
    return (
        <ScrollView style={S.detailPane}>
            <View style={S.detailHeader}>
                <TouchableOpacity onPress={onClose}><Ionicons name="close" size={20} color={T.gray400} /></TouchableOpacity>
                <Text style={S.detailHeaderTitle}>Payout Request</Text>
            </View>
            <View style={S.detailProfile}>
                <Text style={S.detailName}>{item.wallet?.user?.name}</Text>
                <Text style={S.detailSub}>{item.wallet?.user?.phone}</Text>
                <View style={S.detailBalBox}>
                    <Text style={S.detailBalLbl}>Amount Requested</Text>
                    <Text style={[S.detailBalVal, { color: T.goldLight }]}>KES {Number(item.amount).toLocaleString()}</Text>
                </View>
            </View>
            
            {item.status === 'pending_admin' && (
                <View style={S.detailSection}>
                    <Text style={S.detailSectionTitle}>REVIEW ACTION</Text>
                    <View style={{ flexDirection: 'row', gap: 10, marginTop: 10 }}>
                        <TouchableOpacity style={[S.detailSubmitBtn, { flex: 1, backgroundColor: T.green }]} onPress={() => onApprove(item.id)}>
                            <Text style={S.detailSubmitTxt}>Approve & Pay</Text>
                        </TouchableOpacity>
                    </View>
                    <View style={{ marginTop: 24, paddingVertical: 16, borderTopWidth: 1, borderTopColor: T.glassBorder }}>
                        <Text style={S.detailFieldLbl}>Or Reject Request (Provide Reason)</Text>
                        <TextInput style={S.detailInput} placeholder="Rejection reason..." placeholderTextColor={T.gray600} value={reason} onChangeText={setReason} />
                        <TouchableOpacity style={[S.detailSubmitBtn, { backgroundColor: 'rgba(239,68,68,0.15)', borderWidth: 1, borderColor: 'rgba(239,68,68,0.3)' }]} onPress={async () => {
                            if (!reason.trim()) return Alert.alert('Error', 'Reason required to reject');
                            setLoading(true);
                            await rejectAction(item.id, reason);
                            setLoading(false);
                            onClose();
                        }} disabled={loading}>
                            {loading ? <ActivityIndicator color={T.red} size="small" /> : <Text style={[S.detailSubmitTxt, { color: T.red }]}>Reject Payout</Text>}
                        </TouchableOpacity>
                    </View>
                </View>
            )}
        </ScrollView>
    );
};

const LoanDetail = ({ loan, onClose, onAction }) => {
    return (
        <ScrollView style={S.detailPane}>
            <View style={S.detailHeader}>
                <TouchableOpacity onPress={onClose}><Ionicons name="close" size={20} color={T.gray400} /></TouchableOpacity>
                <Text style={S.detailHeaderTitle}>Loan Application</Text>
            </View>
            <View style={S.detailProfile}>
                <Text style={S.detailName}>{loan.user?.name}</Text>
                <Text style={S.detailSub}>{loan.purpose}</Text>
                <View style={{ flexDirection: 'row', gap: 10, marginTop: 16 }}>
                    <View style={[S.detailBalBox, { flex: 1 }]}>
                        <Text style={S.detailBalLbl}>Principal</Text>
                        <Text style={S.detailBalVal}>KES {Number(loan.amount).toLocaleString()}</Text>
                    </View>
                    <View style={[S.detailBalBox, { flex: 1, backgroundColor: 'rgba(239,68,68,0.05)', borderColor: 'rgba(239,68,68,0.2)' }]}>
                        <Text style={S.detailBalLbl}>Total Debt</Text>
                        <Text style={[S.detailBalVal, { color: T.red }]}>KES {Number(loan.total_repayable).toLocaleString()}</Text>
                    </View>
                </View>
            </View>
            
            <View style={S.detailSection}>
                <Text style={S.detailSectionTitle}>TERMS</Text>
                <View style={S.settingInfoCard}><Text style={S.settingInfoKey}>Interest Rate</Text><Text style={[S.settingInfoVal, { fontSize: 13, fontWeight: '600' }]}>{parseFloat(loan.interest_rate)}% flat</Text></View>
                <View style={S.settingInfoCard}><Text style={S.settingInfoKey}>Duration</Text><Text style={[S.settingInfoVal, { fontSize: 13, fontWeight: '600' }]}>{loan.duration_months} month(s)</Text></View>
                <View style={S.settingInfoCard}><Text style={S.settingInfoKey}>Due Date</Text><Text style={[S.settingInfoVal, { fontSize: 13, fontWeight: '600' }]}>{new Date(loan.due_date).toLocaleDateString()}</Text></View>
                <View style={S.settingInfoCard}><Text style={S.settingInfoKey}>Outstanding</Text><Text style={[S.settingInfoVal, { fontSize: 13, fontWeight: '600', color: loan.balance > 0 ? T.red : T.green }]}>KES {Number(loan.balance).toLocaleString()}</Text></View>
            </View>

            {loan.status === 'pending' && (
                <View style={S.detailSection}>
                    <Text style={S.detailSectionTitle}>ACTIONS</Text>
                    <TouchableOpacity style={[S.detailSubmitBtn, { backgroundColor: T.green }]} onPress={() => onAction(loan, 'active')}>
                        <Text style={S.detailSubmitTxt}>Approve & Disburse</Text>
                    </TouchableOpacity>
                    <TouchableOpacity style={[S.detailSubmitBtn, { backgroundColor: T.glass, borderColor: T.red, borderWidth: 1, marginTop: 10 }]} onPress={() => onAction(loan, 'rejected')}>
                        <Text style={[S.detailSubmitTxt, { color: T.red }]}>Reject Application</Text>
                    </TouchableOpacity>
                </View>
            )}
            
            {loan.status === 'active' && new Date(loan.due_date) < new Date() && (
                <View style={S.detailSection}>
                    <Text style={S.detailSectionTitle}>DELINQUENCY MANAGEMENT</Text>
                    <TouchableOpacity style={[S.detailSubmitBtn, { backgroundColor: 'rgba(239,68,68,0.2)' }]} onPress={() => onAction(loan, 'defaulted')}>
                        <Text style={[S.detailSubmitTxt, { color: T.red }]}>Mark as Defaulted</Text>
                    </TouchableOpacity>
                </View>
            )}
        </ScrollView>
    );
};

const ChallengeDetail = ({ challenge, onClose, onUpdate, onEdit }) => {
    const [participants, setParticipants] = useState([]);
    const [loadingPart, setLoadingPart] = useState(true);
    const [expandedPartId, setExpandedPartId] = useState(null);

    useEffect(() => {
        getChallengeSavingsParticipants(challenge.id).then(r => {
            if (r.success) setParticipants(r.data);
            setLoadingPart(false);
        }).catch(() => setLoadingPart(false));
    }, [challenge.id]);

    const handleStatusChange = (newStatus) => {
        Alert.alert('Change Status', `Set challenge to "${newStatus}"?`, [
            { text: 'Cancel', style: 'cancel' },
            { text: 'Confirm', onPress: async () => {
                await updateAdminSavingsChallenge(challenge.id, { status: newStatus });
                onUpdate(true);
            }}
        ]);
    };

    return (
        <ScrollView style={S.detailPane}>
            <View style={S.detailHeader}>
                <TouchableOpacity onPress={onClose}><Ionicons name="close" size={20} color={T.gray400} /></TouchableOpacity>
                <Text style={S.detailHeaderTitle}>Savings Challenge</Text>
            </View>
            <View style={S.detailProfile}>
                <Text style={S.detailName}>{challenge.title}</Text>
                <Text style={S.detailSub}>{challenge.description || 'No description'}</Text>
                <View style={S.detailBalBox}>
                    <Text style={S.detailBalLbl}>Target Amount</Text>
                    <Text style={[S.detailBalVal, { color: T.blueLight }]}>KES {Number(challenge.target_amount).toLocaleString()}</Text>
                </View>
            </View>

            <View style={S.detailSection}>
                <Text style={S.detailSectionTitle}>STATUS CONTROLS</Text>
                <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
                    {['upcoming', 'active', 'completed', 'cancelled'].filter(s => s !== challenge.status).map(s => (
                        <TouchableOpacity key={s} style={[S.statusBtn, { borderColor: T.glassBorder }]} onPress={() => handleStatusChange(s)}>
                            <Text style={[S.statusBtnTxt, { color: T.white }]}>→ Set {s.charAt(0).toUpperCase() + s.slice(1)}</Text>
                        </TouchableOpacity>
                    ))}
                    <TouchableOpacity 
                        style={[S.statusBtn, { borderColor: T.blueLight, backgroundColor: 'rgba(45,140,240,0.1)' }]} 
                        onPress={() => onEdit(challenge)}
                    >
                        <Ionicons name="create-outline" size={12} color={T.blueLight} style={{ marginRight: 4 }} />
                        <Text style={[S.statusBtnTxt, { color: T.blueLight }]}>Edit / Rerun Challenge</Text>
                    </TouchableOpacity>
                </View>
            </View>

            <View style={S.detailSection}>
                <Text style={S.detailSectionTitle}>LEADERBOARD</Text>
                {loadingPart ? <ActivityIndicator color={T.blueLight} style={{ marginVertical: 20 }} /> :
                 participants.length === 0 ? <Text style={S.partEmpty}>No participants yet</Text> : 
                 participants.map((p, i) => (
                    <TouchableOpacity key={p.id} activeOpacity={0.7} onPress={() => setExpandedPartId(expandedPartId === p.id ? null : p.id)}>
                        <View style={[S.partRow, expandedPartId === p.id && { borderBottomWidth: 0 }]}>
                            <Text style={[S.partRank, i < 3 && { color: [T.goldLight, T.gray300, '#CD7F32'][i] }]}>#{i + 1}</Text>
                            <Text style={S.partName}>{p.user?.name || '—'}</Text>
                            <Text style={S.partAmt}>KES {Number(p.saved_amount).toLocaleString()}</Text>
                        </View>
                        {expandedPartId === p.id && (
                            <View style={{ padding: 12, backgroundColor: 'rgba(255,255,255,0.03)', borderBottomLeftRadius: T.rSm, borderBottomRightRadius: T.rSm, marginBottom: 8 }}>
                                <Text style={{ fontSize: 11, color: T.gray400, marginBottom: 4 }}>Phone: <Text style={{ color: T.white }}>{p.user?.phone || 'N/A'}</Text></Text>
                                <Text style={{ fontSize: 11, color: T.gray400, marginBottom: 4 }}>Email: <Text style={{ color: T.white }}>{p.user?.email || 'N/A'}</Text></Text>
                                <Text style={{ fontSize: 11, color: T.gray400 }}>Status: <Text style={{ color: T.white }}>{p.status}</Text></Text>
                            </View>
                        )}
                    </TouchableOpacity>
                 ))}
            </View>
        </ScrollView>
    );
};

const SettingsDetail = ({ onClose, loanSettings, setLoanSettings, savingLoanSettings, handleSaveLoanSettings, newThreshold, setNewThreshold, handleUpdateThreshold, settings }) => {
    const [cfgTab, setCfgTab] = useState('threshold');

    return (
        <ScrollView style={S.detailPane} showsVerticalScrollIndicator={false} contentContainerStyle={{ paddingBottom: 40}}>
            <View style={S.detailHeader}>
                <TouchableOpacity onPress={onClose}><Ionicons name="close" size={20} color={T.gray400} /></TouchableOpacity>
                <Text style={S.detailHeaderTitle}>Global Configuration</Text>
            </View>

            <View style={S.detailProfile}>
                <Text style={S.detailName}>Financial Controls</Text>
                <Text style={S.detailSub}>Modify algorithm limits and global thresholds automatically applied to all users.</Text>
            </View>

            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 8, paddingHorizontal: 2, paddingBottom: 16, marginBottom: 16, borderBottomWidth: 1, borderBottomColor: T.glassBorder }}>
                <TouchableOpacity onPress={() => setCfgTab('threshold')} style={{ paddingVertical: 6, paddingHorizontal: 12, borderBottomWidth: cfgTab === 'threshold' ? 2 : 0, borderBottomColor: T.blueLight }}>
                    <Text style={{ fontSize: 10, fontWeight: '600', color: cfgTab === 'threshold' ? T.white : T.gray500, letterSpacing: 1 }}>APPROVAL THRESHOLD</Text>
                </TouchableOpacity>
                <TouchableOpacity onPress={() => setCfgTab('divisors')} style={{ paddingVertical: 6, paddingHorizontal: 12, borderBottomWidth: cfgTab === 'divisors' ? 2 : 0, borderBottomColor: T.blueLight }}>
                    <Text style={{ fontSize: 10, fontWeight: '600', color: cfgTab === 'divisors' ? T.white : T.gray500, letterSpacing: 1 }}>ALGORITHM DIVISORS</Text>
                </TouchableOpacity>
                <TouchableOpacity onPress={() => setCfgTab('tiers')} style={{ paddingVertical: 6, paddingHorizontal: 12, borderBottomWidth: cfgTab === 'tiers' ? 2 : 0, borderBottomColor: T.blueLight }}>
                    <Text style={{ fontSize: 10, fontWeight: '600', color: cfgTab === 'tiers' ? T.white : T.gray500, letterSpacing: 1 }}>SCORING TIERS</Text>
                </TouchableOpacity>
            </ScrollView>

            {cfgTab === 'threshold' && (
                <View>
                    <Text style={S.detailFieldLbl}>Withdrawal Risk Limit (KES)</Text>
                    <View style={{ flexDirection: 'row', gap: 10, alignItems: 'stretch' }}>
                        <TextInput style={[S.detailInput, { flex: 1, height: 'auto' }]} value={newThreshold} onChangeText={setNewThreshold} keyboardType="numeric" />
                        <TouchableOpacity style={[S.detailSubmitBtn, { marginVertical: 0, paddingHorizontal: 24, backgroundColor: T.violet }]} onPress={handleUpdateThreshold}>
                            <Text style={S.detailSubmitTxt}>Set Limit</Text>
                        </TouchableOpacity>
                    </View>
                    <Text style={[S.settingDesc, { marginTop: 8 }]}>Transactions attempting to withdraw past this limit will immediately halt and queue for manual payout review by an administrator.</Text>
                </View>
            )}

            {cfgTab === 'divisors' && (
                <View>
                    {[
                        { key: 'LOAN_WEIGHT_SAVINGS', label: 'Savings Divisor', placeholder: '500' },
                        { key: 'LOAN_WEIGHT_POINTS', label: 'Points Divisor', placeholder: '10' },
                        { key: 'LOAN_WEIGHT_LEVEL_MULT', label: 'Level Multiplier', placeholder: '5' },
                        { key: 'LOAN_WEIGHT_SALES', label: 'Sales Divisor', placeholder: '1000' },
                        { key: 'LOAN_WEIGHT_PURCHASES_MULT', label: 'Purchase Multiplier', placeholder: '2' },
                        { key: 'LOAN_WEIGHT_ENGAGEMENT', label: 'Engagement Divisor', placeholder: '5' },
                        { key: 'LOAN_INTEREST_RATE', label: 'Interest Rate (%)', placeholder: '5.0' },
                    ].map(field => (
                        <View key={field.key} style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 12 }}>
                            <Text style={[S.detailFieldLbl, { flex: 1.5, marginBottom: 0 }]}>{field.label}</Text>
                            <TextInput style={[S.detailInput, { flex: 1, paddingVertical: 8 }]} placeholder={field.placeholder} placeholderTextColor={T.gray600} keyboardType="numeric" value={loanSettings[field.key] !== undefined ? String(loanSettings[field.key]) : ''} onChangeText={v => setLoanSettings(prev => ({ ...prev, [field.key]: v }))} />
                        </View>
                    ))}
                    <TouchableOpacity style={[S.detailSubmitBtn, { backgroundColor: T.green, marginTop: 12 }]} onPress={handleSaveLoanSettings} disabled={savingLoanSettings}>
                        {savingLoanSettings ? <ActivityIndicator size="small" color={T.white} /> : <Text style={S.detailSubmitTxt}>Save Weights</Text>}
                    </TouchableOpacity>
                </View>
            )}

            {cfgTab === 'tiers' && (
                <View>
                    {[
                        { scoreKey: 'LOAN_SCORE_GOLD', limitKey: 'LOAN_LIMIT_GOLD', label: '🥇 Gold Tier', scoreP: '100', limitP: '50000' },
                        { scoreKey: 'LOAN_SCORE_SILVER', limitKey: 'LOAN_LIMIT_SILVER', label: '🥈 Silver Tier', scoreP: '50', limitP: '15000' },
                        { scoreKey: 'LOAN_SCORE_BRONZE', limitKey: 'LOAN_LIMIT_BRONZE', label: '🥉 Bronze Tier', scoreP: '20', limitP: '5000' },
                        { scoreKey: 'LOAN_SCORE_STARTER', limitKey: 'LOAN_LIMIT_STARTER', label: '🌱 Starter Tier', scoreP: '10', limitP: '2000' },
                    ].map(tier => (
                        <View key={tier.scoreKey} style={{ marginBottom: 16 }}>
                            <Text style={[S.detailFieldLbl, { marginBottom: 6 }]}>{tier.label}</Text>
                            <View style={{ flexDirection: 'row', gap: 10 }}>
                                <TextInput style={[S.detailInput, { flex: 1 }]} placeholder={`Min Score: ${tier.scoreP}`} placeholderTextColor={T.gray600} keyboardType="numeric" value={loanSettings[tier.scoreKey] !== undefined ? String(loanSettings[tier.scoreKey]) : ''} onChangeText={v => setLoanSettings(prev => ({ ...prev, [tier.scoreKey]: v }))} />
                                <TextInput style={[S.detailInput, { flex: 1 }]} placeholder={`KES Lim: ${tier.limitP}`} placeholderTextColor={T.gray600} keyboardType="numeric" value={loanSettings[tier.limitKey] !== undefined ? String(loanSettings[tier.limitKey]) : ''} onChangeText={v => setLoanSettings(prev => ({ ...prev, [tier.limitKey]: v }))} />
                            </View>
                        </View>
                    ))}
                    
                    <TouchableOpacity style={[S.detailSubmitBtn, { backgroundColor: T.green, marginTop: 12 }]} onPress={handleSaveLoanSettings} disabled={savingLoanSettings}>
                        {savingLoanSettings ? <ActivityIndicator size="small" color={T.white} /> : <Text style={S.detailSubmitTxt}>Save Tier Limits</Text>}
                    </TouchableOpacity>
                </View>
            )}
        </ScrollView>
    );
};

const CreateChallengeDetail = ({ onClose, onUpdate, newChallenge, setNewChallenge, challengeLoading, setChallengeLoading }) => {
    return (
        <ScrollView style={S.detailPane} showsVerticalScrollIndicator={false} contentContainerStyle={{ paddingBottom: 40}}>
            <View style={S.detailHeader}>
                <TouchableOpacity onPress={onClose}><Ionicons name="close" size={20} color={T.gray400} /></TouchableOpacity>
                <Text style={S.detailHeaderTitle}>Savings Engine</Text>
            </View>

            <View style={S.detailProfile}>
                <Text style={S.detailName}>New Challenge Event</Text>
                <Text style={S.detailSub}>Draft a new global challenge for users to opt into and accumulate platform points.</Text>
            </View>

            <View style={S.detailSection}>
                <Text style={S.detailSectionTitle}>CHALLENGE PREMISE</Text>
                {[
                    { key: 'title', label: 'Title *', placeholder: 'e.g. March Savings Sprint' },
                    { key: 'description', label: 'Description', placeholder: 'Brief challenge description…' },
                    { key: 'target_amount', label: 'Target Amount (KES) *', placeholder: 'e.g. 5000', keyboard: 'numeric' },
                ].map(field => (
                    <View key={field.key} style={{ marginBottom: 12 }}>
                        <Text style={S.detailFieldLbl}>{field.label}</Text>
                        <TextInput style={S.detailInput} placeholder={field.placeholder} placeholderTextColor={T.gray600} keyboardType={field.keyboard || 'default'} value={newChallenge[field.key]} onChangeText={v => setNewChallenge(prev => ({ ...prev, [field.key]: v }))} />
                    </View>
                ))}
            </View>

            <View style={S.detailSection}>
                <Text style={S.detailSectionTitle}>REWARDS & CADENCE</Text>
                <Text style={S.detailFieldLbl}>Cadence Schedule *</Text>
                <View style={{ flexDirection: 'row', gap: 6, marginBottom: 12 }}>
                    {['daily', 'weekly', 'monthly'].map(c => (
                        <TouchableOpacity key={c} style={[S.cadenceBtn, newChallenge.cadence === c && S.cadenceBtnActive, { flex: 1 }]} onPress={() => setNewChallenge(p => ({ ...p, cadence: c }))}>
                            <Text style={[S.cadenceBtnTxt, newChallenge.cadence === c && { color: T.goldLight }]}>{c.charAt(0).toUpperCase() + c.slice(1)}</Text>
                        </TouchableOpacity>
                    ))}
                </View>

                {[
                    { key: 'xp_reward', label: 'XP Reward', placeholder: 'e.g. 100', keyboard: 'numeric' },
                    { key: 'bonus_description', label: 'Bonus Reward Context', placeholder: 'e.g. Loan tier upgrade' },
                ].map(field => (
                    <View key={field.key} style={{ marginBottom: 12 }}>
                        <Text style={S.detailFieldLbl}>{field.label}</Text>
                        <TextInput style={S.detailInput} placeholder={field.placeholder} placeholderTextColor={T.gray600} keyboardType={field.keyboard || 'default'} value={newChallenge[field.key]} onChangeText={v => setNewChallenge(prev => ({ ...prev, [field.key]: v }))} />
                    </View>
                ))}
            </View>

            <View style={S.detailSection}>
                <Text style={S.detailSectionTitle}>TIMELINE & METADATA</Text>
                {[
                    { key: 'start_date', label: 'Start Date (YYYY-MM-DD) *', placeholder: '2026-04-01' },
                    { key: 'end_date', label: 'End Date (YYYY-MM-DD) *', placeholder: '2026-04-30' },
                    { key: 'max_participants', label: 'Maximum Slots (blank = unlimited)', placeholder: 'e.g. 100', keyboard: 'numeric' },
                ].map(field => (
                    <View key={field.key} style={{ marginBottom: 12 }}>
                        <Text style={S.detailFieldLbl}>{field.label}</Text>
                        <TextInput style={S.detailInput} placeholder={field.placeholder} placeholderTextColor={T.gray600} keyboardType={field.keyboard || 'default'} value={newChallenge[field.key]} onChangeText={v => setNewChallenge(prev => ({ ...prev, [field.key]: v }))} />
                    </View>
                ))}

                <Text style={S.detailFieldLbl}>Badge Identifier Shape</Text>
                <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
                    {['trophy-outline', 'ribbon-outline', 'star-outline', 'flame-outline', 'medal-outline', 'cash-outline', 'wallet-outline', 'rocket-outline'].map(icon => (
                        <TouchableOpacity key={icon} style={[S.iconBtn, newChallenge.badge_icon === icon && S.iconBtnActive, { backgroundColor: T.glass, borderWidth: 1, borderColor: newChallenge.badge_icon === icon ? T.goldLight : T.glassBorder, padding: 8, borderRadius: T.rMd }]} onPress={() => setNewChallenge(p => ({ ...p, badge_icon: icon }))}>
                            <Ionicons name={icon} size={20} color={newChallenge.badge_icon === icon ? T.goldLight : T.gray500} />
                        </TouchableOpacity>
                    ))}
                </View>
            </View>

            <TouchableOpacity
                style={[S.detailSubmitBtn, { backgroundColor: T.green, marginTop: 12 }]}
                disabled={challengeLoading}
                onPress={async () => {
                    if (!newChallenge.title || !newChallenge.target_amount || !newChallenge.start_date || !newChallenge.end_date) {
                        return Alert.alert('Error', 'Fill in required fields (*)');
                    }
                    setChallengeLoading(true);
                    try {
                        await createAdminSavingsChallenge(newChallenge);
                        setNewChallenge({ title: '', description: '', cadence: 'monthly', target_amount: '', xp_reward: '50', bonus_description: '', start_date: '', end_date: '', max_participants: '', badge_icon: 'trophy-outline' });
                        Alert.alert('✓ Created', 'Challenge is now live and visible to users!');
                        onClose();
                        onUpdate(true);
                    } catch (e) {
                        Alert.alert('Error', e?.response?.data?.error || 'Failed to create challenge');
                    } finally { setChallengeLoading(false); }
                }}
            >
                {challengeLoading ? <ActivityIndicator size="small" color={T.white} /> : <Text style={S.detailSubmitTxt}>Create & Publish Challenge</Text>}
            </TouchableOpacity>

        </ScrollView>
    );
};

const EditChallengeDetail = ({ challenge, onClose, onUpdate }) => {
    const [form, setForm] = useState({
        title: challenge.title || '',
        description: challenge.description || '',
        target_amount: String(challenge.target_amount || ''),
        cadence: challenge.cadence || 'monthly',
        xp_reward: String(challenge.xp_reward || '50'),
        bonus_description: challenge.bonus_description || '',
        start_date: challenge.start_date ? challenge.start_date.split('T')[0] : '',
        end_date: challenge.end_date ? challenge.end_date.split('T')[0] : '',
        max_participants: challenge.max_participants ? String(challenge.max_participants) : '',
        badge_icon: challenge.badge_icon || 'trophy-outline'
    });
    const [loading, setLoading] = useState(false);

    const handleSubmit = async () => {
        if (!form.title || !form.target_amount || !form.start_date || !form.end_date) {
            return Alert.alert('Error', 'Fill in required fields (*)');
        }
        setLoading(true);
        try {
            await updateAdminSavingsChallenge(challenge.id, form);
            Alert.alert('✓ Updated', 'Challenge has been updated successfully.');
            onClose();
            onUpdate(true);
        } catch (e) {
            Alert.alert('Error', e?.response?.data?.error || 'Failed to update challenge');
        } finally { setLoading(false); }
    };

    return (
        <ScrollView style={S.detailPane} showsVerticalScrollIndicator={false} contentContainerStyle={{ paddingBottom: 40}}>
            <View style={S.detailHeader}>
                <TouchableOpacity onPress={onClose}><Ionicons name="close" size={20} color={T.gray400} /></TouchableOpacity>
                <Text style={S.detailHeaderTitle}>Edit Challenge</Text>
            </View>

            <View style={S.detailProfile}>
                <Text style={S.detailName}>{challenge.title}</Text>
                <Text style={S.detailSub}>Modify details or rerun this challenge by updating its timeline.</Text>
            </View>

            <View style={S.detailSection}>
                <Text style={S.detailSectionTitle}>CHALLENGE PREMISE</Text>
                {[
                    { key: 'title', label: 'Title *', placeholder: 'e.g. March Savings Sprint' },
                    { key: 'description', label: 'Description', placeholder: 'Brief challenge description…' },
                    { key: 'target_amount', label: 'Target Amount (KES) *', placeholder: 'e.g. 5000', keyboard: 'numeric' },
                ].map(field => (
                    <View key={field.key} style={{ marginBottom: 12 }}>
                        <Text style={S.detailFieldLbl}>{field.label}</Text>
                        <TextInput style={S.detailInput} placeholder={field.placeholder} placeholderTextColor={T.gray600} keyboardType={field.keyboard || 'default'} value={form[field.key]} onChangeText={v => setForm(prev => ({ ...prev, [field.key]: v }))} />
                    </View>
                ))}
            </View>

            <View style={S.detailSection}>
                <Text style={S.detailSectionTitle}>REWARDS & CADENCE</Text>
                <Text style={S.detailFieldLbl}>Cadence Schedule *</Text>
                <View style={{ flexDirection: 'row', gap: 6, marginBottom: 12 }}>
                    {['daily', 'weekly', 'monthly'].map(c => (
                        <TouchableOpacity key={c} style={[S.cadenceBtn, form.cadence === c && S.cadenceBtnActive, { flex: 1 }]} onPress={() => setForm(p => ({ ...p, cadence: c }))}>
                            <Text style={[S.cadenceBtnTxt, form.cadence === c && { color: T.goldLight }]}>{c.charAt(0).toUpperCase() + c.slice(1)}</Text>
                        </TouchableOpacity>
                    ))}
                </View>

                {[
                    { key: 'xp_reward', label: 'XP Reward', placeholder: 'e.g. 100', keyboard: 'numeric' },
                    { key: 'bonus_description', label: 'Bonus Reward Context', placeholder: 'e.g. Loan tier upgrade' },
                ].map(field => (
                    <View key={field.key} style={{ marginBottom: 12 }}>
                        <Text style={S.detailFieldLbl}>{field.label}</Text>
                        <TextInput style={S.detailInput} placeholder={field.placeholder} placeholderTextColor={T.gray600} keyboardType={field.keyboard || 'default'} value={form[field.key]} onChangeText={v => setForm(prev => ({ ...prev, [field.key]: v }))} />
                    </View>
                ))}
            </View>

            <View style={S.detailSection}>
                <Text style={S.detailSectionTitle}>TIMELINE & METADATA</Text>
                {[
                    { key: 'start_date', label: 'Start Date (YYYY-MM-DD) *', placeholder: '2026-04-01' },
                    { key: 'end_date', label: 'End Date (YYYY-MM-DD) *', placeholder: '2026-04-30' },
                    { key: 'max_participants', label: 'Maximum Slots (blank = unlimited)', placeholder: 'e.g. 100', keyboard: 'numeric' },
                ].map(field => (
                    <View key={field.key} style={{ marginBottom: 12 }}>
                        <Text style={S.detailFieldLbl}>{field.label}</Text>
                        <TextInput style={S.detailInput} placeholder={field.placeholder} placeholderTextColor={T.gray600} keyboardType={field.keyboard || 'default'} value={form[field.key]} onChangeText={v => setForm(prev => ({ ...prev, [field.key]: v }))} />
                    </View>
                ))}

                <Text style={S.detailFieldLbl}>Badge Identifier Shape</Text>
                <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
                    {['trophy-outline', 'ribbon-outline', 'star-outline', 'flame-outline', 'medal-outline', 'cash-outline', 'wallet-outline', 'rocket-outline'].map(icon => (
                        <TouchableOpacity key={icon} style={[S.iconBtn, form.badge_icon === icon && S.iconBtnActive, { backgroundColor: T.glass, borderWidth: 1, borderColor: form.badge_icon === icon ? T.goldLight : T.glassBorder, padding: 8, borderRadius: T.rMd }]} onPress={() => setForm(p => ({ ...p, badge_icon: icon }))}>
                            <Ionicons name={icon} size={20} color={form.badge_icon === icon ? T.goldLight : T.gray500} />
                        </TouchableOpacity>
                    ))}
                </View>
            </View>

            <TouchableOpacity
                style={[S.detailSubmitBtn, { backgroundColor: T.blueLight, marginTop: 12 }]}
                disabled={loading}
                onPress={handleSubmit}
            >
                {loading ? <ActivityIndicator size="small" color={T.white} /> : <Text style={S.detailSubmitTxt}>Update Challenge Details</Text>}
            </TouchableOpacity>

        </ScrollView>
    );
};

// ─────────────────────────────────────────────────────────────
// AUDIT & ALERTS COMPONENTS
// ─────────────────────────────────────────────────────────────

const AuditCard = ({ log, onSelect, isSelected }) => {
    const isError = log.status === 'failed';
    const initials = (log.affectedUser?.name || '?')[0].toUpperCase();
    
    return (
        <TouchableOpacity 
            style={[S.txCard, isSelected && { borderColor: T.blueLight, backgroundColor: 'rgba(59,130,246,0.05)' }]} 
            onPress={onSelect} activeOpacity={0.8}
        >
            <View style={S.txRow}>
                <View style={[S.txAvatar, { backgroundColor: isError ? 'rgba(239,68,68,0.1)' : 'rgba(45,140,240,0.1)' }]}>
                    <Text style={[S.txAvatarTxt, { color: isError ? T.red : T.blueLight }]}>{initials}</Text>
                </View>
                <View style={{ flex: 1 }}>
                    <Text style={S.txName}>{log.affectedUser?.name || 'Unknown User'}</Text>
                    <Text style={S.txPhone}>{log.event_type}</Text>
                    <Text style={S.txDate}>{new Date(log.createdAt).toLocaleString()}</Text>
                </View>
                <View style={{ alignItems: 'flex-end', gap: 4 }}>
                    <Text style={[S.txAmount, { color: T.white, fontSize: 13 }]}>
                        KES {Number(log.amount || 0).toLocaleString()}
                    </Text>
                    {log.flagged ? (
                        <View style={[S.pill, { backgroundColor: 'rgba(239,68,68,0.15)', borderColor: 'rgba(239,68,68,0.3)' }]}>
                            <Ionicons name="warning" size={8} color={T.red} />
                            <Text style={[S.pillTxt, { color: T.red }]}>FLAGGED</Text>
                        </View>
                    ) : (
                        <View style={[S.pill, { backgroundColor: isError ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)', borderColor: isError ? 'rgba(239,68,68,0.2)' : 'rgba(16,185,129,0.2)' }]}>
                            <Text style={[S.pillTxt, { color: isError ? T.red : T.green }]}>{isError ? 'FAILED' : 'SUCCESS'}</Text>
                        </View>
                    )}
                </View>
            </View>
        </TouchableOpacity>
    );
};

const AlertCard = ({ alert, onSelect, isSelected }) => {
    const initials = (alert.affectedUser?.name || '?')[0].toUpperCase();
    return (
        <TouchableOpacity 
            style={[S.txCard, { borderColor: 'rgba(239,68,68,0.3)' }, isSelected && { backgroundColor: 'rgba(239,68,68,0.05)' }]} 
            onPress={onSelect} activeOpacity={0.8}
        >
            <View style={[S.txAccentTop, { backgroundColor: T.red }]} />
            <View style={S.txRow}>
                <View style={[S.txAvatar, { backgroundColor: 'rgba(239,68,68,0.15)' }]}>
                    <Ionicons name="warning" size={16} color={T.red} />
                </View>
                <View style={{ flex: 1 }}>
                    <Text style={S.txName}>{alert.event_type}</Text>
                    <Text style={[S.txPhone, { color: '#fca5a5' }]}>{alert.flag_reason}</Text>
                    <Text style={S.txDate}>{new Date(alert.createdAt).toLocaleString()}</Text>
                </View>
                <View style={{ alignItems: 'flex-end', gap: 4 }}>
                    <Text style={[S.txAmount, { color: T.goldLight }]}>
                        KES {Number(alert.amount || 0).toLocaleString()}
                    </Text>
                    <Text style={{ fontSize: 9, color: T.gray500 }}>{alert.affectedUser?.phone || 'No Phone'}</Text>
                </View>
            </View>
        </TouchableOpacity>
    );
};

const AuditDetail = ({ log, onClose }) => {
    return (
        <ScrollView style={S.detailPane}>
            <View style={S.detailHeader}>
                <TouchableOpacity onPress={onClose}><Ionicons name="close" size={20} color={T.gray400} /></TouchableOpacity>
                <Text style={S.detailHeaderTitle}>Audit Record Details</Text>
            </View>

            <View style={S.detailProfile}>
                <View style={[S.detailAvatar, { backgroundColor: 'rgba(45,140,240,0.1)' }]}>
                    <Ionicons name="document-text-outline" size={20} color={T.blueLight} />
                </View>
                <Text style={S.detailName}>{log.event_type}</Text>
                <Text style={S.detailSub}>{new Date(log.createdAt).toLocaleString()}</Text>
                <View style={[S.detailBalBox, { flexDirection: 'row', justifyContent: 'space-between', paddingHorizontal: 20 }]}>
                    <View>
                        <Text style={S.detailBalLbl}>Amount ({log.currency})</Text>
                        <Text style={S.detailBalVal}>KES {Number(log.amount || 0).toLocaleString()}</Text>
                    </View>
                    <View style={{ alignItems: 'flex-end' }}>
                        <Text style={S.detailBalLbl}>Status</Text>
                        <Text style={[S.detailBalVal, { color: log.status === 'success' ? T.green : T.red }]}>{log.status?.toUpperCase()}</Text>
                    </View>
                </View>
            </View>

            <View style={S.detailSection}>
                <Text style={S.detailSectionTitle}>CONTEXT & IDENTIFIERS</Text>
                <View style={S.settingInfoCard}><Text style={S.settingInfoKey}>Log ID</Text><Text style={[S.settingInfoVal, { fontSize: 13 }]}>{log.id}</Text></View>
                <View style={S.settingInfoCard}><Text style={S.settingInfoKey}>Affected User</Text><Text style={[S.settingInfoVal, { fontSize: 13 }]}>{log.affectedUser?.name || 'N/A'} ({log.affectedUser?.email || 'No email'})</Text></View>
                <View style={S.settingInfoCard}><Text style={S.settingInfoKey}>Actor</Text><Text style={[S.settingInfoVal, { fontSize: 13 }]}>{log.actor ? `${log.actor.name} (${log.actor.role})` : 'System'}</Text></View>
                <View style={S.settingInfoCard}><Text style={S.settingInfoKey}>Entity Reference</Text><Text style={[S.settingInfoVal, { fontSize: 13, color: T.blueLight }]}>{log.entity_type} #{log.entity_id || 'N/A'}</Text></View>
                {log.mpesa_receipt && <View style={S.settingInfoCard}><Text style={S.settingInfoKey}>M-Pesa Receipt</Text><Text style={[S.settingInfoVal, { fontSize: 13, color: T.goldLight }]}>{log.mpesa_receipt}</Text></View>}
            </View>

            <View style={S.detailSection}>
                <Text style={S.detailSectionTitle}>SECURITY DATA</Text>
                <View style={S.settingInfoCard}><Text style={S.settingInfoKey}>IP Address</Text><Text style={[S.settingInfoVal, { fontSize: 13 }]}>{log.ip_address || 'Internal/Unknown'}</Text></View>
                <View style={S.settingInfoCard}><Text style={S.settingInfoKey}>User Agent</Text><Text style={[S.settingInfoVal, { fontSize: 11, fontWeight: '400', color: T.gray400 }]}>{log.user_agent || 'N/A'}</Text></View>
                <View style={S.settingInfoCard}><Text style={S.settingInfoKey}>Payload Hash (SHA-256)</Text><Text style={[S.settingInfoVal, { fontSize: 10, fontWeight: '400', color: T.gray500 }]} numberOfLines={2}>{log.request_body_hash || 'N/A'}</Text></View>
            </View>

            {log.flagged && (
                <View style={[S.detailSection, { backgroundColor: 'rgba(239,68,68,0.05)', padding: 12, borderRadius: T.rMd, borderWidth: 1, borderColor: 'rgba(239,68,68,0.2)' }]}>
                    <Text style={[S.detailSectionTitle, { color: T.red }]}>SECURITY FLAG DETECTED</Text>
                    <Text style={{ color: T.white, fontSize: 12, marginBottom: 8 }}>{log.flag_reason}</Text>
                    <Text style={{ color: '#fca5a5', fontSize: 11 }}>This transaction was flagged by the anomaly detection engine. Review via the Alerts tab.</Text>
                </View>
            )}

            {log.notes && (
                <View style={S.detailSection}>
                    <Text style={S.detailSectionTitle}>ADMIN/SYSTEM NOTES</Text>
                    <Text style={{ color: T.gray300, fontSize: 12, lineHeight: 18 }}>{log.notes}</Text>
                </View>
            )}
        </ScrollView>
    );
};

const AlertDetail = ({ alert, onClose, onResolve }) => {
    const [notes, setNotes] = useState('');
    const [loading, setLoading] = useState(false);

    const handleResolve = async () => {
        if (!notes.trim()) return Alert.alert('Error', 'Please enter resolution notes before clearing this flag.');
        setLoading(true);
        await onResolve(alert.id, notes);
        setLoading(false);
        onClose();
    };

    return (
        <ScrollView style={S.detailPane}>
            <View style={S.detailHeader}>
                <TouchableOpacity onPress={onClose}><Ionicons name="close" size={20} color={T.gray400} /></TouchableOpacity>
                <Text style={S.detailHeaderTitle}>Security Alert Triage</Text>
            </View>

            <View style={S.detailProfile}>
                <View style={[S.detailAvatar, { backgroundColor: 'rgba(239,68,68,0.15)' }]}>
                    <Ionicons name="warning" size={24} color={T.red} />
                </View>
                <Text style={S.detailName}>{alert.flag_reason}</Text>
                <Text style={S.detailSub}>Logged: {new Date(alert.createdAt).toLocaleString()}</Text>
            </View>

            <View style={S.detailSection}>
                <Text style={S.detailSectionTitle}>TRANSACTION CONTEXT</Text>
                <View style={S.settingInfoCard}><Text style={S.settingInfoKey}>Event Type</Text><Text style={[S.settingInfoVal, { fontSize: 13 }]}>{alert.event_type}</Text></View>
                <View style={S.settingInfoCard}><Text style={S.settingInfoKey}>Amount Affected</Text><Text style={[S.settingInfoVal, { fontSize: 13, color: T.goldLight }]}>KES {Number(alert.amount || 0).toLocaleString()}</Text></View>
                <View style={S.settingInfoCard}><Text style={S.settingInfoKey}>User Affected</Text><Text style={[S.settingInfoVal, { fontSize: 13 }]}>{alert.affectedUser?.name} ({alert.affectedUser?.phone})</Text></View>
                {alert.mpesa_receipt && <View style={S.settingInfoCard}><Text style={S.settingInfoKey}>M-Pesa Trace</Text><Text style={[S.settingInfoVal, { fontSize: 13 }]}>{alert.mpesa_receipt}</Text></View>}
            </View>

            <View style={S.detailSection}>
                <Text style={S.detailSectionTitle}>TRIAGE & RESOLUTION</Text>
                <Text style={S.detailFieldLbl}>Investigation Notes (Required)</Text>
                <TextInput 
                    style={[S.detailInput, { height: 80, textAlignVertical: 'top' }]} 
                    placeholder="E.g. Verified with user over phone. Manual adjustment approved." 
                    placeholderTextColor={T.gray600} 
                    multiline 
                    value={notes} 
                    onChangeText={setNotes} 
                />
                
                <TouchableOpacity 
                    style={[S.detailSubmitBtn, { backgroundColor: T.green }]} 
                    onPress={handleResolve} 
                    disabled={loading}
                >
                    {loading ? <ActivityIndicator size="small" color={T.white} /> : 
                        <Text style={S.detailSubmitTxt}>Mark as Resolved & Safe</Text>
                    }
                </TouchableOpacity>

                {alert.entity_type === 'wallet' && (
                    <Text style={{ marginTop: 16, fontSize: 11, color: T.gray500, textAlign: 'center' }}>
                        Need to reverse this? Use the Wallets tab to manually debit the user's account.
                    </Text>
                )}
            </View>
        </ScrollView>
    );
};

// ─────────────────────────────────────────────────────────────
// STYLES — landing-page inspired
// ─────────────────────────────────────────────────────────────

export default FinancialManagementScreen;

